import { useEffect, useMemo } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import MarkdownLite, { slugify } from "@/components/MarkdownLite";
import { POSTS, getPostBySlug, getRelatedPosts } from "@/data/blogPosts";
import {
  ArrowLeft,
  ArrowRight,
  Clock,
  Calendar,
  ShareNetwork,
} from "@phosphor-icons/react";
import { toast } from "sonner";

function formatDateFR(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

// Extract H2 titles to build an anchored table of contents.
function extractHeadings(body) {
  const lines = body.split("\n");
  return lines
    .filter((l) => l.startsWith("## "))
    .map((l) => {
      const title = l.slice(3).trim();
      return { title, id: slugify(title) };
    });
}

export default function BlogPost() {
  const { slug } = useParams();
  const post = getPostBySlug(slug);

  const headings = useMemo(() => (post ? extractHeadings(post.body) : []), [post]);
  const related = useMemo(() => (post ? getRelatedPosts(post.slug) : []), [post]);

  // Inject BlogPosting JSON-LD for rich Google results.
  useEffect(() => {
    if (!post) return;
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "post-jsonld";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: post.title,
      description: post.excerpt,
      inLanguage: "fr-FR",
      datePublished: post.date,
      dateModified: post.date,
      author: {
        "@type": "Organization",
        name: "Doudou Alert",
        url: "https://doudou-alert.fr",
      },
      publisher: {
        "@type": "Organization",
        name: "Doudou Alert",
        logo: {
          "@type": "ImageObject",
          url: "https://doudou-alert.fr/logo.svg",
        },
      },
      mainEntityOfPage: {
        "@type": "WebPage",
        "@id": `https://doudou-alert.fr/blog/${post.slug}`,
      },
      keywords: post.keywords,
    });
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById("post-jsonld");
      if (el) el.remove();
    };
  }, [post]);

  if (!post) return <Navigate to="/blog" replace />;

  const share = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({ title: post.title, text: post.excerpt, url });
      } catch (e) {
        if (e?.name !== "AbortError") toast.error("Partage impossible");
      }
    } else {
      try {
        await navigator.clipboard.writeText(url);
        toast.success("Lien copié !");
      } catch {
        window.prompt("Copiez ce lien :", url);
      }
    }
  };

  return (
    <Layout>
      <SEO title={post.title} description={post.excerpt} />

      <article className="max-w-3xl mx-auto px-4 sm:px-6 py-10" data-testid={`blog-post-${post.slug}`}>
        <Link
          to="/blog"
          className="inline-flex items-center gap-2 text-orange-600 font-bold hover:text-orange-700 mb-8"
          data-testid="blog-post-back"
        >
          <ArrowLeft size={16} weight="bold" /> Retour au blog
        </Link>

        <div className="flex flex-wrap items-center gap-3 text-xs font-bold uppercase tracking-widest text-orange-500 mb-4">
          <span className="px-3 py-1 rounded-full bg-orange-50 border border-orange-100">
            {post.category}
          </span>
          <span className="flex items-center gap-1 text-stone-500">
            <Calendar size={13} weight="bold" /> {formatDateFR(post.date)}
          </span>
          <span className="flex items-center gap-1 text-stone-500">
            <Clock size={13} weight="bold" /> {post.readingMinutes} min de lecture
          </span>
        </div>

        <h1 className="font-display text-4xl sm:text-5xl font-black text-stone-800 leading-tight" data-testid="blog-post-title">
          {post.title}
        </h1>

        <p className="mt-5 text-lg text-stone-600 leading-relaxed">{post.excerpt}</p>

        {/* Share + TOC */}
        <div className="mt-8 flex flex-wrap items-center gap-3">
          <button
            onClick={share}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-rose-50 text-rose-600 font-bold hover:bg-rose-100 transition text-sm"
            data-testid="blog-post-share"
          >
            <ShareNetwork size={16} weight="bold" /> Partager
          </button>
        </div>

        {headings.length > 2 && (
          <nav
            className="mt-8 p-5 rounded-2xl bg-stone-50 border border-stone-100"
            aria-labelledby="toc-title"
          >
            <p id="toc-title" className="text-xs font-bold uppercase tracking-widest text-stone-500 mb-3">
              Sommaire
            </p>
            <ol className="space-y-1.5 text-stone-700">
              {headings.map((h, i) => (
                <li key={h.id} className="leading-relaxed">
                  <a
                    href={`#${h.id}`}
                    className="hover:text-orange-600 hover:underline decoration-orange-300"
                  >
                    <span className="text-orange-500 font-bold mr-2">{i + 1}.</span>
                    {h.title}
                  </a>
                </li>
              ))}
            </ol>
          </nav>
        )}

        <div className="mt-8">
          <MarkdownLite source={post.body} />
        </div>

        {/* Tags */}
        <div className="mt-12 pt-8 border-t border-stone-200 flex flex-wrap gap-2">
          {post.tags.map((t) => (
            <span
              key={t}
              className="px-3 py-1 text-xs font-bold rounded-full bg-orange-50 text-orange-700 border border-orange-100"
            >
              #{t}
            </span>
          ))}
        </div>

        {/* Related */}
        {related.length > 0 && (
          <section className="mt-16" aria-labelledby="related-title">
            <p className="text-xs font-bold uppercase tracking-widest text-orange-500 mb-3">
              À lire aussi
            </p>
            <h2 id="related-title" className="font-display text-2xl font-black text-stone-800 mb-6">
              D'autres guides qui peuvent vous aider
            </h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to={`/blog/${r.slug}`}
                  className="group block p-5 rounded-2xl bg-white border border-rose-100 hover:border-orange-200 hover:shadow-lg transition"
                  data-testid={`blog-related-${r.slug}`}
                >
                  <p className="text-[11px] font-bold uppercase tracking-widest text-orange-500 mb-1">
                    {r.category}
                  </p>
                  <h3 className="font-display text-base font-black text-stone-800 leading-snug group-hover:text-orange-600">
                    {r.title}
                  </h3>
                  <span className="inline-flex items-center gap-1 mt-2 text-sm font-bold text-orange-600 group-hover:translate-x-0.5 transition-transform">
                    Lire <ArrowRight size={14} weight="bold" />
                  </span>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Final CTA */}
        <div className="mt-16 rounded-3xl bg-gradient-to-br from-orange-500 to-rose-500 text-white p-8 md:p-10 text-center shadow-xl shadow-orange-200/50">
          <h2 className="font-display text-2xl md:text-3xl font-black">
            Signalez votre doudou perdu dès maintenant
          </h2>
          <p className="text-white/90 mt-3 max-w-xl mx-auto">
            Chaque minute compte. Créez une fiche en 2 minutes et augmentez vos chances de le retrouver.
          </p>
          <Link to="/inscription">
            <button className="mt-6 bg-white text-orange-600 hover:bg-stone-50 rounded-full px-8 py-3 font-bold shadow-lg" data-testid="blog-post-cta">
              Créer mon alerte gratuite
            </button>
          </Link>
        </div>
      </article>
    </Layout>
  );
}
